// Low-level DSP primitives and tuning constants for IR synthesis.
//
// The numbers here are ported from the legacy auralization engine, which was
// hand-calibrated against REW ground-truth measurements. Treat the calibration
// constants (VELVET_DENSITY, IR_PEAK_TARGET, the RT60 clamps) as "known good"
// unless you are re-measuring.

import type { BandRt60 } from './types';

/** 3·ln(10): converts an RT60 (−60 dB time) into a decay rate, decay = e^(−rate·t). */
export const LN10_3 = 3 * Math.log(10);

/** Speed of sound (m/s), for the HF air-absorption extrapolation. */
export const SPEED_OF_SOUND = 343;

/**
 * Schroeder crossover. Below this the room is modal (additive damped sinusoids
 * driven by the per-frequency transfer function); above it the field is diffuse
 * (velvet-noise tail driven by per-band RT60). 200 Hz is not arbitrary: it is
 * exactly where the backend's eigensolve stops (BARE_EIGENSOLVE_FMAX_HZ) and
 * where its RT60 pipeline hands off from modal to ray (_merge_at_schroeder).
 */
export const MODAL_CROSSOVER_HZ = 200;

/**
 * The modal curve is re-referenced (divided) by its geometric-mean magnitude
 * over [MODAL_ANCHOR_LO_HZ, MODAL_CROSSOVER_HZ] so that the band joins the
 * diffuse tail continuously at the crossover. This removes the absolute Pa
 * scale of the voxel freq_response while preserving its peak/null coloration.
 */
export const MODAL_ANCHOR_LO_HZ = 100;

/** RT60 clamps for the modal region (s). Needle-thin or numerically-noisy
 *  decays are clipped softly. */
export const MODAL_RT60_FLOOR_S = 0.05;
export const MODAL_RT60_CEIL_S = 5.0;

/** Third-octave bandpass Q whose −3 dB edges sit on the band edges. */
export const THIRD_OCTAVE_Q = 4.318;

/** Velvet-noise impulse density (impulses/sec) for the diffuse tail. The
 *  canonical 2000/s (Karjalainen et al. 2007): dense enough to avoid clicky
 *  HF, sparse enough to avoid metallic fizz. */
export const VELVET_DENSITY = 2000;

/** Default IR length (s). 2 s comfortably contains small-room decays. */
export const IR_LENGTH_SEC = 2.0;

/** Peak amplitude the synthesized IR is allowed to reach. Below 1.0 so the
 *  post-convolution clip-protect has headroom. */
export const IR_PEAK_TARGET = 0.5;

/** Crude air-absorption coefficient — only fires for tail bands above the
 *  highest backend-traced band (i.e. >~10 kHz extrapolation). decayRate gains
 *  COEFF·(fc² − fmax²)·c. */
export const AIR_ABSORPTION_COEFF = 1.0e-10;

/** Standard third-octave band centers (Hz), 20 Hz … 16 kHz. */
export const THIRD_OCTAVE_CENTERS: readonly number[] = [
  20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400, 500, 630,
  800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000, 6300, 8000, 10000,
  12500, 16000,
];

/** Target spectral envelope for the diffuse tail: flat ≤ 1 kHz, −3 dB/oct
 *  above (approximates small-room HF rolloff from air + per-bounce absorption). */
export function tailTarget(fc: number): number {
  return fc <= 1000 ? 1.0 : Math.sqrt(1000 / fc);
}

/** Clamp helper. */
export function clamp(x: number, lo: number, hi: number): number {
  return x < lo ? lo : x > hi ? hi : x;
}

/**
 * Velvet noise: one ±1 impulse per fixed-size grid cell, at a random offset
 * with a random sign. Flat magnitude spectrum but sparse in time, so feeding
 * it through a bandpass yields discrete decaying-sinusoid wavelets (reverb
 * echoes) rather than dense filter ring.
 */
export function velvetNoise(
  numSamples: number,
  sampleRate: number,
  density: number,
  rand: () => number,
): Float32Array {
  const out = new Float32Array(numSamples);
  const grid = sampleRate / density;
  for (let g = 0; ; g++) {
    const cellStart = Math.floor(g * grid);
    if (cellStart >= numSamples) break;
    const cellEnd = Math.min(numSamples, Math.floor((g + 1) * grid));
    const cellSize = cellEnd - cellStart;
    if (cellSize <= 0) break;
    const pos = cellStart + Math.floor(rand() * cellSize);
    if (pos < numSamples) out[pos] = rand() < 0.5 ? 1.0 : -1.0;
  }
  return out;
}

/** In-place RBJ bandpass biquad (constant-skirt, 0 dB peak). */
export function biquadBandpassInPlace(
  buf: Float32Array,
  centerFreq: number,
  sampleRate: number,
  Q: number,
): void {
  const w0 = (2 * Math.PI * centerFreq) / sampleRate;
  const cosw0 = Math.cos(w0);
  const sinw0 = Math.sin(w0);
  const alpha = sinw0 / (2.0 * Q);
  const a0 = 1 + alpha;
  const B0 = alpha / a0;
  const B2 = -alpha / a0;
  const A1 = (-2 * cosw0) / a0;
  const A2 = (1 - alpha) / a0;
  let x1 = 0, x2 = 0, y1 = 0, y2 = 0;
  for (let i = 0; i < buf.length; i++) {
    const x = buf[i];
    const y = B0 * x + B2 * x2 - A1 * y1 - A2 * y2;
    buf[i] = y;
    x2 = x1; x1 = x;
    y2 = y1; y1 = y;
  }
}

/** Linear-in-log-frequency interpolation of parallel (freqs, values) arrays.
 *  Clamps to the endpoints outside the tabulated range. */
export function interpLogFreq(
  freqs: number[],
  values: number[],
  f: number,
): number {
  const n = freqs.length;
  if (n === 0) return 0;
  if (f <= freqs[0]) return values[0];
  if (f >= freqs[n - 1]) return values[n - 1];
  for (let i = 1; i < n; i++) {
    if (freqs[i] >= f) {
      const fLo = freqs[i - 1], fHi = freqs[i];
      const vLo = values[i - 1], vHi = values[i];
      const u = (Math.log(f) - Math.log(fLo)) / (Math.log(fHi) - Math.log(fLo));
      return vLo + u * (vHi - vLo);
    }
  }
  return values[n - 1];
}

/** Convert a third-octave RT60 dict to a sorted numeric-keyed pair of arrays. */
export function bandsToArrays(bands: BandRt60): { freqs: number[]; rt60: number[] } {
  const freqs = Object.keys(bands)
    .map(Number)
    .filter((f) => !isNaN(f) && f > 0)
    .sort((a, b) => a - b);
  return { freqs, rt60: freqs.map((f) => bands[String(f)]) };
}

/** Geometric mean of `mag` over the points whose freq is within [lo, hi].
 *  Returns 1 if no points fall in the band (a no-op reference). */
export function geomeanInBand(
  freqs: number[],
  mag: number[],
  lo: number,
  hi: number,
): number {
  let logSum = 0;
  let count = 0;
  for (let i = 0; i < freqs.length; i++) {
    const f = freqs[i];
    if (f >= lo && f <= hi && mag[i] > 0) {
      logSum += Math.log(mag[i]);
      count++;
    }
  }
  if (count === 0) {
    // Fall back to geomean over all positive points.
    for (let i = 0; i < mag.length; i++) {
      if (mag[i] > 0) { logSum += Math.log(mag[i]); count++; }
    }
  }
  return count > 0 ? Math.exp(logSum / count) : 1;
}
